import { Download, ExternalLink } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { client } from '../lib/sanity';
import Button from '../components/ui/Button';

const cvQuery = `*[_type == "cvPage"][0]{
    fileName,
    "cvUrl": cvFile.asset->url
}`;

export default function CV() {
    const [cvData, setCvData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    const cvUrl = cvData?.cvUrl;
    const fileName = cvData?.fileName || 'Curriculum_Vitae.pdf';

    useEffect(() => {
        async function fetchCV() {
            try {
                const data = await client.fetch(cvQuery);

                if (data && data.cvUrl) {
                    setCvData(data);
                } else {
                    setError('The CV is temporarily unavailable.');
                }
            } catch (err) {
                console.error('Failed to fetch CV data from Sanity:', err);
                setError('The CV is temporarily unavailable.');
            } finally {
                setIsLoading(false);
            }
        }

        fetchCV();
    }, []);

    const handleDownloadPDF = async () => {
        try {
            const response = await fetch(cvUrl);
            const blob = await response.blob();
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
        } catch (err) {
            console.error('Download failed:', err);
            window.open(cvUrl, '_blank');
        }
    };

    if (isLoading) {
        return (
            <div className="h-screen bg-white dark:bg-zinc-950 flex items-center justify-center">
                <p className="text-xl font-mono text-accent">Loading CV.</p>
            </div>
        );
    }

    if (error || !cvUrl) {
        return (
            <div className="h-screen bg-white dark:bg-zinc-950 flex flex-col items-center justify-center p-4">
                <p className="text-xl text-red-600 dark:text-red-500 mb-2">Could not load the CV.</p>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 max-w-lg text-center mb-6">
                    {error || 'Please try again in a moment.'}
                </p>
                <Button as={Link} to="/contact">Get in touch instead</Button>
            </div>
        );
    }

    return (
        <div className="h-screen bg-white dark:bg-zinc-950 overflow-hidden">
            <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:pl-10 lg:pr-6 pt-4 md:pt-6 pb-4 h-full flex flex-col -mt-16">
                <h2 className="text-4xl font-serif font-extrabold mb-6 text-black dark:text-white tracking-tight flex-shrink-0">
                    Curriculum Vitae
                </h2>

                <div className="w-full flex-grow border-2 border-black dark:border-white shadow-brutal-lg overflow-hidden bg-zinc-100 dark:bg-zinc-900 transition-all duration-300">
                    <iframe
                        src={`${cvUrl}#zoom=100`}
                        title={`Embedded PDF Viewer for ${fileName}`}
                        className="w-full h-full"
                        allowFullScreen
                        loading="lazy"
                    >
                        <div className="p-8 text-center bg-white dark:bg-zinc-950 h-full flex flex-col items-center justify-center">
                            <p className="text-lg text-zinc-700 dark:text-zinc-300 mb-4">
                                PDF viewer unavailable.
                            </p>
                            <p className="text-base text-zinc-600 dark:text-zinc-400 mb-6">
                                Your browser is preventing the embedded viewer from loading, or it does not support it.
                            </p>
                            <Button onClick={handleDownloadPDF} aria-label={`Download ${fileName} as a direct fallback`}>
                                <Download className="w-5 h-5 mr-2" />
                                Download PDF
                            </Button>
                        </div>
                    </iframe>
                </div>

                <div className="flex justify-center gap-4 mt-6 flex-shrink-0">
                    <Button onClick={handleDownloadPDF} aria-label={`Download ${fileName}`}>
                        <Download className="w-5 h-5 mr-2" />
                        Download PDF
                    </Button>
                    <Button as="a" href={cvUrl} target="_blank" rel="noopener noreferrer" variant="secondary" aria-label="View PDF in new tab">
                        <ExternalLink className="w-5 h-5 mr-2" />
                        Open in New Tab
                    </Button>
                </div>

                <div className="pb-4" />
            </section>
        </div>
    );
}
